// Web-portal session recovery (audit 2026-09-27 M6).
//
// 🔴 The web portal called logout() on ANY non-OK /api/user/profile response and on any
// network error, and never used the server's refresh grace on load. So a couple was
// signed out every ~7 days (the token simply expired between visits — nginx: 228 × 401
// on the profile vs 124 refresh calls in 30 days) and on every server blip (3 × 500
// during the 2026-09-05 outage each logged someone out). The rule now:
//   401            → try ONE refresh; only if that fails (or the refreshed token also
//                    gets 401) end the session;
//   5xx / network  → keep the token and the last-known user and let the next load retry.

export type ProfileFailure = 'unauthorized' | 'server' | 'network';
export type ProfileFailureAction = 'refresh' | 'logout' | 'keep';

export function classifyProfileStatus(status: number): ProfileFailure {
  return status === 401 ? 'unauthorized' : 'server';
}

/** `alreadyRefreshed` = the failing token is the one a refresh just produced. */
export function decideProfileFailure(kind: ProfileFailure, alreadyRefreshed: boolean): ProfileFailureAction {
  if (kind !== 'unauthorized') return 'keep';
  return alreadyRefreshed ? 'logout' : 'refresh';
}

/** JWT `exp` in ms, or null if the token is not a decodable JWT. Never verifies. */
export function tokenExpiryMs(token: string | null | undefined): number | null {
  try {
    const part = String(token ?? '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const json = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
    const exp = Number(JSON.parse(json)?.exp);
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Refresh when the token expires within `withinMs` (default 2 days) or already has. */
export function tokenNeedsRefresh(token: string | null | undefined, now = Date.now(), withinMs = 2 * 24 * 60 * 60 * 1000): boolean {
  const exp = tokenExpiryMs(token);
  return exp !== null && exp - now < withinMs;
}

/** Collapse concurrent calls into one in-flight promise (focus + interval + 401 at once). */
export function singleFlight<T>(fn: () => Promise<T>): () => Promise<T> {
  let inflight: Promise<T> | null = null;
  return () => {
    if (!inflight) inflight = fn().finally(() => { inflight = null; });
    return inflight;
  };
}
